import { AnyRoute, createRoute, lazyRouteComponent, notFound, redirect } from '@tanstack/react-router'
import { BankApplicationsRouterPaths } from 'commons/constants/routes'
import { getApplicationWebPage } from 'commons/service/bank/application'
import { ApiError } from 'commons/types'
import { ApplicationWebPageDetail } from 'commons/types/DTO/bank'
import { ApplicationEditRoute } from 'onboarding-form'

import { BankOnboardingWrapper } from './BankOnboardingWrapper'
import { ChangesDetailsRoute } from './routes/ApplicationChangesDetails/route'
import { DocumentCreateRoute } from './routes/ApplicationDocumentCreate/route'
import { DocumentDetailsRoute } from './routes/ApplicationDocumentDetails/route'
import { DocumentEditRoute } from './routes/ApplicationDocumentEdit/route'
import { ListRoute } from './routes/ApplicationList/route'

export const routeOnboardingTree = <T extends AnyRoute>(rootRoute: T) => {
  const OnboardingIndexRoute = createRoute({
    beforeLoad: async ({ context }) => {
      if (!context.accessibleRoutingFlags.onboardingRootAccess) {
        throw redirect({
          to: '/'
        })
      }
    },
    getParentRoute: () => rootRoute,
    path: BankApplicationsRouterPaths.INDEX,
    component: BankOnboardingWrapper
  })

  const DetailsRoute = <T extends AnyRoute>(indexRoute: T) =>
    createRoute({
      getParentRoute: () => indexRoute,
      path: '$applicationId',
      beforeLoad: ({ params, search, ...all }) => {
        if ('applicationId' in params) return { ...all, flatDeps: { id: Number(params.applicationId), ...search } }
        else throw notFound()
      },
      loader: async ({ params }) => {
        let app: ApplicationWebPageDetail

        try {
          app = await getApplicationWebPage(+params.applicationId)
        } catch (e: unknown) {
          if (e instanceof ApiError && e.httpCode === 400) {
            throw notFound()
          }
          throw e
        }
        return app
      },
      component: lazyRouteComponent(() => import('./routes/ApplicationDetails/ApplicationDetails')),
      shouldReload: (p) => p.cause === 'enter'
    })

  return OnboardingIndexRoute.addChildren([
    ApplicationEditRoute<typeof OnboardingIndexRoute>(OnboardingIndexRoute),
    DetailsRoute<typeof OnboardingIndexRoute>(OnboardingIndexRoute),
    ListRoute<typeof OnboardingIndexRoute>(OnboardingIndexRoute),
    DocumentCreateRoute<typeof OnboardingIndexRoute>(OnboardingIndexRoute),
    DocumentEditRoute<typeof OnboardingIndexRoute>(OnboardingIndexRoute),
    DocumentDetailsRoute<typeof OnboardingIndexRoute>(OnboardingIndexRoute),
    ChangesDetailsRoute<typeof OnboardingIndexRoute>(OnboardingIndexRoute)
  ])
}
